import React, { useCallback, useEffect, useRef, useState } from "react";
import { Route, Switch } from "react-router-dom";

import { Stack } from "@mui/material";
import { AsyncContentMui } from "components";
import { Chart, InfoCard } from "components/reports";
import { ReportMeasuresTable } from "components/reports/ReportMeasuresTable/ReportMeasuresTable";
import { Routes } from "constants/routes";
import { useActionHook } from "hooks";
import {
  useRefetchReportsSelect,
  useReportsActions,
  useReportsQueriesSelector,
  useReportsTableSelector,
  useSnapshotCubicConfigSelector
} from "store";
import { ReportTableColumn } from "store/types";

import { ClassicTable } from "../../components/reports/Classic-table/ClassicTable";

const isReorderOnly = (prev: ReportTableColumn[], curr: ReportTableColumn[]) => {
  const prevKeys = prev.map((c) => c.key);
  const currKeys = curr.map((c) => c.key);

  const sameSet = prevKeys.length === currKeys.length && prevKeys.every((k) => currKeys.includes(k));

  if (!sameSet) return false;

  const sameOrder = prevKeys.every((k, i) => k === currKeys[i]);

  return !sameOrder;
};

export const Snapshot: React.FC = () => {
  const [metaLoading, setMetaLoading] = useState<boolean>(true);
  const {
    dateRange,
    exchangeMethod,
    measures,
    groupBy,
    columnBy,
    calculatedFields,
    indexName,
    rowBy,
    currency,
    originalCurrency,
    filters,
    classicTableColumns,
    reportTableColumns,
    characteristics,
    use_accounting_date,
    graphDataFormation
  } = useReportsQueriesSelector();
  const cubicSettings = useSnapshotCubicConfigSelector();
  const refetchReport = useRefetchReportsSelect();
  const tableData = useReportsTableSelector();
  const prevColumnsRef = useRef<ReportTableColumn[] | null>(null);

  const {
    getReportTableAction,
    getCubicAction,
    getMetaAction,
    refetchReportAction,
    setQueriesAction,
    getBulkPieChartsAction
  } = useReportsActions();

  const getMetaAndSettings = useCallback(async () => {
    try {
      if (!metaLoading) {
        setMetaLoading(true);
      }
      await Promise.allSettled([getMetaAction()]);
    } catch (e) {
      console.log(e);
    } finally {
      setMetaLoading(false);
    }
  }, [metaLoading, getMetaAction]);

  useEffect(() => {
    getMetaAndSettings();
  }, []);

  const { action: refetch, loading } = useActionHook(() =>
    getReportTableAction({
      dateRange,
      measures,
      groupBy,
      columnBy: location.pathname === Routes.snapshot ? columnBy : "measure",
      // Selected parameters — including calculated fields — are always sent as table columns
      // (reportTableColumns / classicTableColumns), so we never send calculatedFields separately.
      // This also prevents a mislabeled characteristic in calculatedFields from corrupting the payload.
      calculatedFields: [],
      indexName,
      rowBy,
      currency,
      originalCurrency,
      characteristics,
      filters,
      exchangeMethod,
      columns: location.pathname === Routes.snapshot ? reportTableColumns : classicTableColumns,
      use_accounting_date,
      show_count_rows: true
    })
  );

  const {
    data: cubicData,
    action: getCubic,
    loading: cubicLoading //pageType: cubicSnapshot, cubicDeal
  } = useActionHook(getCubicAction);

  const {
    data: bulkChartsData = {},
    action: fetchBulkCharts,
    loading: bulkChartsLoading
  } = useActionHook(getBulkPieChartsAction);

  useEffect(() => {
    if (Object.keys(cubicSettings).length) {
      getCubic({
        cubics: cubicSettings,
        pageType: "cubicSnapshot",
        filters,
        exchangeMethod,
        currency,
        dateRange,
        use_accounting_date
      });
    }
  }, [cubicSettings, dateRange, exchangeMethod, currency, filters, use_accounting_date]);

  useEffect(() => {
    if (refetchReport === "portfolio") {
      refetch();
      refetchReportAction(null);
    }
  }, [refetchReport]);

  useEffect(() => {
    if (!reportTableColumns) return;

    const prev = prevColumnsRef.current;
    const curr = reportTableColumns;

    if (prev && isReorderOnly(prev, curr)) {
      prevColumnsRef.current = curr;
      return; // skip refetch
    }

    refetch();

    prevColumnsRef.current = curr;
  }, [
    exchangeMethod,
    dateRange,
    measures,
    groupBy,
    columnBy,
    calculatedFields,
    indexName,
    rowBy,
    currency,
    originalCurrency,
    characteristics,
    filters,
    classicTableColumns,
    reportTableColumns,
    use_accounting_date,
    location.pathname
  ]);

  useEffect(() => {
    fetchBulkCharts({
      filters,
      graphDataFormation,
      dateRange,
      currency,
      exchangeMethod,
      use_accounting_date
    });
  }, [filters, graphDataFormation, dateRange, currency, exchangeMethod, use_accounting_date]);

  return (
    <>
      <AsyncContentMui isLoading={metaLoading} spinnerProps={{ size: 150 }}>
        <Stack direction="row" sx={{ gap: "10px", justifyContent: "space-between", width: "100%" }}>
          {cubicData &&
            Object.entries(cubicData).map(([name, data], index) => (
              <InfoCard pageType="cubicSnapshot" isLoading={cubicLoading} name={name} {...data} key={index} />
            ))}
        </Stack>
        <Stack direction="row" sx={{ gap: "10px" }}>
          {Object.entries(bulkChartsData).map(([name, data]) => (
            <Chart
              key={name}
              name={name}
              chartSettings={graphDataFormation[name]}
              filters={filters}
              pageType="chartSnapshot"
              isLoading={bulkChartsLoading}
              data={data}
            />
          ))}
        </Stack>
        <Switch>
          <Route path={Routes.snapshot}>
            <ReportMeasuresTable isLoading={loading} refetchReport={refetch} />
          </Route>
          <Route path={Routes.classic}>
            <ClassicTable
              title="Classic Asset View"
              isLoading={loading}
              data={tableData}
              originalCurrency={originalCurrency}
              columns={classicTableColumns}
              onChangeColumns={(columns) => setQueriesAction({ classicTableColumns: columns })}
              onChangeOriginalCurrency={(originalCurrency) => setQueriesAction({ originalCurrency: originalCurrency })}
            />
          </Route>
        </Switch>
      </AsyncContentMui>
    </>
  );
};
